// src/components/admin/AdminPanel.tsx
import React, { useState } from "react";
import { Box, Alert, Typography } from "@mui/material";
import { useUserRole } from "../../hooks/useUserRole";
import UsersTab from "./UsersTab";
import TeamsTab from "./TeamsTab";
import WorkspacesTab from "./WorkspacesTab";
import AuditLogTab from "./AuditLogTab";

const TABS = [
  { key: "users", label: "Users & Roles" },
  { key: "teams", label: "Teams" },
  { key: "workspaces", label: "Workspaces" },
  { key: "audit", label: "Audit Log" },
];

const AdminPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const { role, isAdmin, isLoading } = useUserRole();

  if (isLoading) {
    return (
      <div style={{ minHeight: "100%", background: "#F7F7F7", padding: "32px" }}>
        <Typography sx={{ fontFamily: "'Inter', sans-serif", color: "#6B7280" }}>Loading...</Typography>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ minHeight: "100%", background: "#F7F7F7", padding: "32px" }}>
        <Alert severity="error" sx={{ borderRadius: "8px", fontFamily: "'Inter', sans-serif" }}>
          You do not have permission to access the Admin Panel. Contact a Super Admin for access.
        </Alert>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100%", background: "#F7F7F7", padding: "32px" }}>
      {/* Page Header */}
      <Box sx={{ mb: "32px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "1.5rem",
              fontWeight: 600,
              color: "#111111",
              letterSpacing: "-0.3px",
            }}
          >
            Admin Panel
          </Typography>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ cursor: "pointer", flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </Box>
        <Typography
          sx={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "0.875rem",
            color: "#6B7280",
            mt: "4px",
          }}
        >
          Manage users, teams, and workspaces. Your role:{" "}
          <strong style={{ color: "#111111" }}>{role.replace("_", " ")}</strong>
        </Typography>
      </Box>

      {/* Tab Navigation */}
      <Box sx={{ display: "flex", alignItems: "center", borderBottom: "1px solid #EAEAEA", mb: "24px" }}>
        {TABS.map((t, idx) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(idx)}
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "0.875rem",
              fontWeight: activeTab === idx ? 600 : 500,
              color: activeTab === idx ? "#EF2D3E" : "#6B7280",
              background: "none",
              border: "none",
              borderBottom: activeTab === idx ? "2px solid #EF2D3E" : "2px solid transparent",
              padding: "10px 16px",
              cursor: "pointer",
              marginBottom: -1,
              transition: "color 0.15s",
              whiteSpace: "nowrap",
            }}
          >
            {t.label}
          </button>
        ))}
      </Box>

      {/* Tab Content */}
      <Box sx={{ background: "#fff", border: "1px solid #EAEAEA", borderRadius: "12px", p: "24px" }}>
        {activeTab === 0 && <UsersTab />}
        {activeTab === 1 && <TeamsTab />}
        {activeTab === 2 && <WorkspacesTab />}
        {activeTab === 3 && <AuditLogTab />}
      </Box>
    </div>
  );
};

export default AdminPanel;
